import { ChatApi } from "./types";

/** Default public window key and DOM/storage prefix. */
export const DEFAULT_NAMESPACE = "tomis";

const NAMESPACE_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

let resolvedNamespace: string | null = null;

function readNamespaceFromScript(): string | undefined {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.dataset.api) {
    return current.dataset.api;
  }

  const scripts =
    document.querySelectorAll<HTMLScriptElement>("script[data-api]");
  for (const script of scripts) {
    const src = script.getAttribute("src") || "";
    if (src.includes("load.js")) {
      return script.dataset.api;
    }
  }

  if (scripts.length === 1) {
    return scripts[0].dataset.api;
  }

  return undefined;
}

/** Vite dev convenience (`VITE_CHAT_API=yonder npm run dev`). Unused in prod builds. */
function readNamespaceFromEnv(): string | undefined {
  const fromEnv = import.meta.env.VITE_CHAT_API;
  if (typeof fromEnv === "string" && fromEnv.trim()) {
    return fromEnv.trim();
  }
  return undefined;
}

function resolveNamespace(): string {
  const raw = (readNamespaceFromScript() ?? readNamespaceFromEnv())?.trim();
  if (!raw) {
    return DEFAULT_NAMESPACE;
  }

  if (!NAMESPACE_PATTERN.test(raw)) {
    console.warn(
      `[${DEFAULT_NAMESPACE}] Invalid data-api "${raw}". Using "${DEFAULT_NAMESPACE}".`,
    );
    return DEFAULT_NAMESPACE;
  }

  return raw;
}

/** Window key / DOM / storage prefix. Resolved once when the loader runs. */
export function getNamespace(): string {
  if (resolvedNamespace === null) {
    resolvedNamespace = resolveNamespace();
  }
  return resolvedNamespace;
}

export function chatLogPrefix(): string {
  return `[${getNamespace()}]`;
}

/** Host-page element ids and data attributes for the active namespace. */
export function getChatDomIds() {
  const ns = getNamespace();
  return {
    host: `${ns}-chat-widget`,
    embed: `${ns}-chat-embed`,
    app: `${ns}-chat-widget-app`,
    closed: `${ns}-chat-closed`,
    open: `${ns}-chat-open`,
    versionAttr: `data-${ns}-version`,
    hostMarkerAttr: `data-${ns}-chat-host-marker`,
  };
}

function windowRecord(): Record<string, unknown> {
  return window as unknown as Record<string, unknown>;
}

/**
 * Attach the chat API to `window[namespace]`.
 *
 * Does not overwrite an existing non-API value (e.g. `window.yonder` already
 * used by the host page). Does not assign `window.tomis` when the namespace
 * is something else.
 */
export function attachChatApiToWindow(api: ChatApi): boolean {
  const ns = getNamespace();
  const existing = windowRecord()[ns];

  if (existing !== undefined && existing !== api) {
    console.warn(
      `${chatLogPrefix()} window.${ns} is already defined. Chat widget API was not attached.`,
    );
    return false;
  }

  windowRecord()[ns] = api;
  return true;
}
